import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangOpen(false);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
  };

  const currentLang = i18n.language;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <div className="text-xl sm:text-2xl font-light text-white tracking-[0.2em] cursor-pointer" onClick={() => scrollToSection('hero')}>
            DEEPQUEST
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8 lg:space-x-12">
            <button onClick={() => scrollToSection('about')} className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-light cursor-pointer">
              About
            </button>
            <button onClick={() => scrollToSection('projects')} className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-light cursor-pointer">
              Projects
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-white transition-colors duration-300 text-sm font-light cursor-pointer">
              Contact
            </button>
            
            {/* Language Switcher */}
            <div className="relative">
              <button 
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors duration-300 text-sm font-light cursor-pointer"
              >
                <i className="ri-global-line text-base"></i>
                <span>{currentLang === 'ar' ? 'العربية' : 'English'}</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${isLangOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isLangOpen && (
                <div className="absolute top-full right-0 mt-2 bg-slate-800 rounded-lg shadow-xl border border-gray-700 overflow-hidden min-w-[120px]">
                  <button
                    onClick={() => changeLanguage('en')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-700 transition-colors duration-200 cursor-pointer ${
                      currentLang === 'en' ? 'text-violet-400 bg-slate-700/50' : 'text-gray-300'
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage('ar')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-700 transition-colors duration-200 cursor-pointer ${
                      currentLang === 'ar' ? 'text-violet-400 bg-slate-700/50' : 'text-gray-300'
                    }`}
                  >
                    العربية
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white w-8 h-8 flex items-center justify-center cursor-pointer"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden py-4 space-y-4 border-t border-gray-700/50">
            <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-300 hover:text-white transition-colors duration-300 text-sm font-light py-2 cursor-pointer">
              About
            </button>
            <button onClick={() => scrollToSection('projects')} className="block w-full text-left text-gray-300 hover:text-white transition-colors duration-300 text-sm font-light py-2 cursor-pointer">
              Projects
            </button>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-300 hover:text-white transition-colors duration-300 text-sm font-light py-2 cursor-pointer">
              Contact
            </button>
            
            {/* Mobile Language Switcher */}
            <div className="pt-2 border-t border-gray-700/50">
              <button
                onClick={() => changeLanguage('en')}
                className={`block w-full text-left py-2 text-sm cursor-pointer ${
                  currentLang === 'en' ? 'text-violet-400' : 'text-gray-300'
                }`}
              >
                English
              </button>
              <button
                onClick={() => changeLanguage('ar')}
                className={`block w-full text-left py-2 text-sm cursor-pointer ${
                  currentLang === 'ar' ? 'text-violet-400' : 'text-gray-300'
                }`}
              >
                العربية
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}

export default Header;